import type React from 'react'
import {createContext, use, useCallback, useMemo, useRef, useState} from 'react'

import {useIsCursorEnabled} from '../hooks/use-cursor-navigation'
import {useSearchTerm, useSetSearchTerm} from './SearchTermContext'

type FindInFileOpenContextType = {
  findInFileOpen: boolean
  setFindInFileOpen: (open: boolean) => void
  searchTerm: string
  setSearchTerm: (term: string) => void
}

const FindInFileOpenContext = createContext<FindInFileOpenContextType>({
  findInFileOpen: false,
  setFindInFileOpen: () => undefined,
  searchTerm: '',
  setSearchTerm: () => undefined,
})

/**
 * This is equivalent to a simple setState, except we save the search state of the find in file panel when it's closed
 * so we can reopen with the last used value
 */
export function FindInFileOpenProvider({children, isBlame}: {children: React.ReactNode; isBlame: boolean}) {
  const searchTerm = useSearchTerm()
  const setSearchTerm = useSetSearchTerm()
  const cursorEnabled = useIsCursorEnabled(isBlame)
  const lastSearchTerm = useRef<string>('')
  const [findInFileOpen, setRawFindInFileOpen] = useState<boolean>(false)
  const setFindInFileOpen = useCallback(
    (open: boolean) => {
      if (open && searchTerm === '' && lastSearchTerm.current !== '') {
        setSearchTerm(lastSearchTerm.current)
      } else if (!open && searchTerm !== '') {
        lastSearchTerm.current = searchTerm
        setSearchTerm('')
      }

      setRawFindInFileOpen(open)
    },
    [searchTerm, setSearchTerm],
  )

  const contextValue: FindInFileOpenContextType = useMemo(() => {
    return {
      findInFileOpen: cursorEnabled ? false : findInFileOpen,
      setFindInFileOpen,
      setSearchTerm,
      searchTerm,
    }
  }, [findInFileOpen, setFindInFileOpen, searchTerm, setSearchTerm, cursorEnabled])

  return <FindInFileOpenContext value={contextValue}>{children}</FindInFileOpenContext>
}

export function useFindInFileOpen() {
  return use(FindInFileOpenContext)
}
